import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';

const ScheduledDeliveryManager = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await complianceReportsCustomizationService?.getAllScheduledDeliveries();
      setSchedules(data);
    } catch (err) {
      setError(err?.message || 'Failed to load scheduled deliveries');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (scheduleId, currentStatus) => {
    try {
      await complianceReportsCustomizationService?.updateScheduledDelivery(scheduleId, {
        isActive: !currentStatus
      });
      await loadSchedules();
    } catch (err) {
      setError(err?.message || 'Failed to update schedule');
    }
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly'
    };
    return labels?.[frequency] || frequency;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Clock" size={20} />
          Scheduled Report Deliveries
        </h3>
        <Button variant="primary" size="sm" iconName="Plus">
          New Schedule
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {schedules?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No scheduled deliveries configured</p>
          <Button variant="outline" size="sm" className="mt-4" iconName="Plus">
            Create First Schedule
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules?.map((schedule) => (
            <div
              key={schedule?.id}
              className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-foreground">{schedule?.scheduleName}</h4>
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        schedule?.isActive
                          ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      {schedule?.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                      {getFrequencyLabel(schedule?.deliveryFrequency)}
                    </span>
                  </div>

                  {schedule?.template && (
                    <p className="text-xs text-muted-foreground mb-2">
                      Template: {schedule?.template?.templateName}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>Next: {formatDate(schedule?.nextDeliveryDate)}</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Icon name="FileText" size={14} />
                      <span>{schedule?.reportFormat?.toUpperCase()}</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Icon name="Mail" size={14} />
                      <span>{schedule?.recipientEmails?.length || 0} recipients</span>
                    </div>
                  </div>

                  {schedule?.recipientDepartments?.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">Departments:</span>
                      <div className="flex items-center gap-1">
                        {schedule?.recipientDepartments?.map((dept) => (
                          <span
                            key={dept}
                            className="text-xs bg-card text-foreground px-2 py-0.5 rounded"
                          >
                            {dept}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToggleActive(schedule?.id, schedule?.isActive)}
                  >
                    <Icon name={schedule?.isActive ? 'Pause' : 'Play'} size={16} />
                  </Button>
                  <Button variant="ghost" size="icon">
                    <Icon name="Edit" size={16} />
                  </Button>
                  <Button variant="ghost" size="icon">
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduledDeliveryManager;